import { useCallback, useMemo } from "react"

import { getExtendedSellableMinutes, isHaulageApplicableRoute } from "@/lib/haulageUtils"
import { operatorOn } from "@/lib/operatorAllocations"
import {
  expandRoutesForDate,
  findRouteInstanceOn,
  routeMatchesSearch,
} from "@/lib/plannedRouteMapping"
import {
  calculateEmptyLegsDetailed,
  resolveRouteKind,
  segmentLoadStates,
} from "@/lib/routeCostCalculation"
import type { CollectionsConfig, PlannedRoute, RouteInstance } from "@/types/collections"

/** routesForDate: every route the date runs (the slots panel reads this). filteredRoutes: what the timeline draws. */
export function useVisibleRoutes({
  routes,
  selectedDate,
  routeInstances,
  searchTerm,
  filterEmptyLegOnly,
  filterHaulageApplicable,
  showStandbyRoutes,
  config,
  getVolumeForecastForStop,
  operatorScope,
}: {
  routes: PlannedRoute[]
  selectedDate: Date
  routeInstances: RouteInstance[] | undefined
  searchTerm: string
  filterEmptyLegOnly: boolean
  filterHaulageApplicable: boolean
  showStandbyRoutes: boolean
  config: CollectionsConfig | undefined
  getVolumeForecastForStop: (route: PlannedRoute, stop: { locationId?: string }) => number
  /** A partner's operator: only the routes it runs on this date are shown. */
  operatorScope?: string | null
}) {
  /** Who runs a route on the selected date: that day's instance, else the plan and its allocations. */
  const dayOperator = useCallback(
    (route: PlannedRoute) =>
      findRouteInstanceOn(routeInstances, route.id, selectedDate)?.operator ||
      operatorOn(route, selectedDate),
    [routeInstances, selectedDate],
  )

  const routeHasEmptyLeg = useCallback(
    (route: PlannedRoute) => {
      const stops = route.stops || []
      const legs = segmentLoadStates(stops, (i) => getVolumeForecastForStop(route, stops[i]) || 0)
      return legs.includes("empty")
    },
    [getVolumeForecastForStop],
  )

  const { routesForDate, filteredRoutes } = useMemo(() => {
    const result = expandRoutesForDate(routes, selectedDate, routeInstances).filter(
      (route) => !operatorScope || dayOperator(route) === operatorScope,
    )
    const matched = new Set(
      routes.filter((route) => routeMatchesSearch(route, searchTerm)).map((route) => route.id),
    )

    const searched = result.filter((route) => matched.has(route.id))

    let visible = searched
    if (filterEmptyLegOnly) {
      visible = searched.filter(routeHasEmptyLeg)
    } else if (filterHaulageApplicable) {
      const driverMaxHours = config?.driver_max_hours_per_day ?? 12
      const minEmptyPct = config?.haulage_min_empty_pct ?? 10
      const minExtendedMinutes = config?.haulage_min_extended_minutes ?? 60
      visible = searched.filter((route) => {
        const emptyDetail = calculateEmptyLegsDetailed(
          route.stops,
          (i) => getVolumeForecastForStop(route, route.stops[i]) ?? 0,
        )
        const extended = getExtendedSellableMinutes(route, driverMaxHours)
        return isHaulageApplicableRoute(route, {
          hasEmptyLegs: emptyDetail.count > 0,
          emptyPct: emptyDetail.emptyPct,
          extendedBeforeFirstMinutes: extended.extendedBeforeFirstMinutes,
          extendedAfterLastMinutes: extended.extendedAfterLastMinutes,
          minEmptyPct,
          minExtendedMinutes,
        })
      })
    }

    if (!showStandbyRoutes) {
      visible = visible.filter((route) => resolveRouteKind(route.route_kind) !== "standby")
    }

    /** Stand-by rows go below every real run, via a stable partition so the order above is unchanged. */
    const operationalRows = visible.filter(
      (route) => resolveRouteKind(route.route_kind) !== "standby",
    )
    const standbyRows = visible.filter(
      (route) => resolveRouteKind(route.route_kind) === "standby",
    )
    visible = [...operationalRows, ...standbyRows]

    return { routesForDate: result, filteredRoutes: visible }
  }, [
    routes,
    selectedDate,
    searchTerm,
    filterEmptyLegOnly,
    filterHaulageApplicable,
    showStandbyRoutes,
    routeHasEmptyLeg,
    routeInstances,
    config?.driver_max_hours_per_day,
    config?.haulage_min_empty_pct,
    config?.haulage_min_extended_minutes,
    getVolumeForecastForStop,
    operatorScope,
    dayOperator,
  ])

  /** The two columns align only by ROW_HEIGHT, so the divider is a border on this row in both. */
  const firstStandbyRowIndex = useMemo(
    () => filteredRoutes.findIndex((route) => resolveRouteKind(route.route_kind) === "standby"),
    [filteredRoutes],
  )

  return { routesForDate, filteredRoutes, firstStandbyRowIndex, dayOperator }
}
